import React from 'react';
import { 
  MapPin,
  Compass,
  Tent,
  Mountain,
  Calendar,
  Users,
  Footprints,
  Backpack,
  Sunrise,
  TreePine,
  Waves,
  Binoculars,
  CheckCircle2,
  Mail,
  Phone
} from 'lucide-react';

const ExpeditionAdventureTemplate = ({ quote, onPayment, isAgentPreview = false }) => {
  if (!quote) {
    return null;
  }

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    // Parse date in local timezone to avoid UTC offset issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get icon for component type - Adventure themed
  const getAdventureIcon = (component) => {
    const type = component.type?.toLowerCase() || '';
    const title = component.title?.toLowerCase() || '';
    
    if (type.includes('hotel') || title.includes('lodge') || title.includes('camp')) {
      return <Tent className="w-6 h-6 text-green-700" />;
    } else if (type.includes('activity') || title.includes('trek') || title.includes('hike')) {
      return <Mountain className="w-6 h-6 text-green-700" />;
    } else if (type.includes('safari') || title.includes('wildlife') || title.includes('tour')) {
      return <Binoculars className="w-6 h-6 text-green-700" />;
    } else if (type.includes('water') || title.includes('river') || title.includes('kayak')) {
      return <Waves className="w-6 h-6 text-green-700" />;
    } else if (type.includes('park') || title.includes('forest') || title.includes('jungle')) {
      return <TreePine className="w-6 h-6 text-green-700" />;
    }
    return <MapPin className="w-6 h-6 text-green-700" />;
  };

  // Get client name
  const getClientName = () => {
    if (quote.client?.firstName && quote.client?.lastName) {
      return `${quote.client.firstName} ${quote.client.lastName}`;
    }
    return 'Adventurer';
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header - Trail Guide Style */}
      <div className="bg-gradient-to-br from-green-900 via-green-800 to-green-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="flex items-center gap-3 mb-4">
            <Compass className="w-8 h-8 text-amber-400" />
            <span className="text-amber-400 font-bold uppercase tracking-wider text-sm">Expedition Itinerary</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            {quote.quoteTitle || 'Your Adventure Awaits'}
          </h1>
          
          <div className="flex flex-wrap gap-6 text-green-100">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              <span>{getClientName()}</span>
            </div>
            
            {quote.travelDates?.start && (
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>
                  {formatDate(quote.travelDates.start)}
                  {quote.travelDates.end && ` - ${formatDate(quote.travelDates.end)}`}
                </span>
              </div>
            )}
            
            {(quote.travelers?.adults || quote.travelers?.children?.length) && (
              <div className="flex items-center gap-2">
                <Backpack className="w-5 h-5" />
                <span>
                  {quote.travelers.adults || 0} Explorer{quote.travelers.adults !== 1 ? 's' : ''}
                  {quote.travelers.children?.length > 0 && ` + ${quote.travelers.children.length} Junior${quote.travelers.children.length !== 1 ? 's' : ''}`}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Trail Guide Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        
        {/* Trail Overview */}
        <div className="mb-12 bg-white rounded-lg border-2 border-dashed border-stone-300 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Mountain className="w-6 h-6 text-green-800" />
            <h2 className="text-2xl font-bold text-green-900">Trail Map</h2>
          </div>
          <p className="text-stone-600 leading-relaxed">
            Follow your expedition route below. Each stop represents a unique experience on your adventure.
          </p>
        </div>

        {/* Trail Stops - Connected with Dashed Lines */}
        {quote.components && quote.components.length > 0 ? (
          <div className="relative">
            {/* Vertical dashed trail line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 border-l-2 border-dashed border-stone-300"></div>

            {/* Trail Stops */}
            <div className="space-y-8">
              {quote.components.map((component, index) => (
                <div key={component.id || index} className="relative pl-20">
                  {/* Trail Marker */}
                  <div className="absolute left-4 top-8 w-8 h-8 bg-green-700 rounded-full border-4 border-stone-50 shadow-lg flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{index + 1}</span>
                  </div>

                  {/* Stop Card */}
                  <div className="bg-white rounded-lg border-2 border-stone-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                    {/* Card Header */}
                    <div className="bg-gradient-to-r from-green-50 to-stone-50 px-6 py-5 border-b-2 border-dashed border-stone-200">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="mt-1">
                            {getAdventureIcon(component)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-bold uppercase tracking-wide rounded-full">
                                Stop {index + 1}
                              </span>
                            </div>
                            <h3 className="text-xl font-bold text-green-900 mb-1">
                              {component.title || `Adventure Stop ${index + 1}`}
                            </h3>
                            <div className="text-sm text-stone-600 font-medium">
                              {component.type || 'Experience'}
                            </div>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-2xl font-bold text-green-800">
                            {formatCurrency(component.price)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Body - Trail Details */}
                    {component.data && Object.keys(component.data).length > 0 && (
                      <div className="px-6 py-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {Object.entries(component.data).map(([key, value]) => {
                            if (!value || key === 'isOption' || key === 'optionDescription') return null;
                            
                            // Check if this is a price field
                            const isPriceField = key.toLowerCase().includes('price') || 
                                                 key.toLowerCase().includes('cost') || 
                                                 key.toLowerCase().includes('fee') ||
                                                 key.toLowerCase().includes('total') ||
                                                 key.toLowerCase().includes('deposit');
                            const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                            const isLocation = key.toLowerCase().includes('location') || 
                                              key.toLowerCase().includes('destination') ||
                                              key.toLowerCase().includes('place');
                            
                            let displayValue = String(value);
                            if (isPriceField && isNumeric) {
                              displayValue = formatCurrency(parseFloat(value));
                            }
                            
                            return (
                              <div key={key} className="flex items-start gap-2">
                                {isLocation && <MapPin className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />}
                                <div className="flex-1">
                                  <div className="text-xs uppercase tracking-wide text-stone-500 font-semibold mb-1">
                                    {key.replace(/_/g, ' ')}
                                  </div>
                                  <div className="text-stone-900 font-medium">
                                    {displayValue}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Component Photos */}
                    {component.photos && component.photos.length > 0 && (
                      <div className="px-6 pb-5">
                        <h4 className="text-sm font-bold text-stone-900 mb-3 uppercase tracking-wide flex items-center gap-2">
                          <span>📸</span> Photos
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {component.photos.map((photo, photoIndex) => (
                            <div 
                              key={photoIndex} 
                              className="relative group cursor-pointer rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow border-2 border-stone-300 bg-white"
                              onClick={() => window.open(photo.url, '_blank')}
                            >
                              <img 
                                src={photo.url} 
                                alt={photo.name || 'Component photo'} 
                                className="w-full h-auto object-contain"
                                style={{ maxHeight: '300px' }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                                <span className="text-white text-sm font-bold">{photo.name || 'View Photo'}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Component Documents */}
                    {component.documents && component.documents.length > 0 && (
                      <div className="px-6 pb-5">
                        <h4 className="text-sm font-bold text-stone-900 mb-3 uppercase tracking-wide flex items-center gap-2">
                          <span>📋</span> Documents
                        </h4>
                        <div className="space-y-2">
                          {component.documents.map((doc, docIndex) => (
                            <a 
                              key={docIndex}
                              href={doc.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 bg-stone-100 hover:bg-stone-200 px-4 py-2 rounded-xl transition-colors border-2 border-stone-300"
                            >
                              <span className="text-lg">📄</span>
                              <span className="text-stone-900 text-sm font-bold">{doc.name}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Trail End Marker */}
            <div className="relative pl-20 mt-8">
              <div className="absolute left-4 top-0 w-8 h-8 bg-amber-500 rounded-full border-4 border-stone-50 shadow-lg flex items-center justify-center">
                <Sunrise className="w-4 h-4 text-white" />
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border-2 border-dashed border-amber-300 px-6 py-4">
                <p className="text-amber-900 font-semibold flex items-center gap-2">
                  <Footprints className="w-5 h-5" />
                  Journey Complete
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg border-2 border-dashed border-stone-300 p-12 text-center">
            <Mountain className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-500">No trail stops added to this expedition yet</p>
          </div>
        )}

        {/* Field Notes Section */}
        {quote.notes && (
          <div className="mt-12 bg-white rounded-lg border-2 border-stone-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Compass className="w-6 h-6 text-green-800" />
              <h3 className="text-xl font-bold text-green-900">Field Notes</h3>
            </div>
            <p className="text-stone-700 leading-relaxed whitespace-pre-wrap">
              {quote.notes}
            </p>
          </div>
        )}

        {/* Expedition Investment Summary */}
        <div className="mt-12 bg-gradient-to-br from-green-900 to-green-800 rounded-lg shadow-xl overflow-hidden">
          <div className="px-8 py-6 border-b border-green-700">
            <h3 className="text-2xl font-bold text-white flex items-center gap-3">
              <Backpack className="w-6 h-6" />
              Expedition Investment
            </h3>
          </div>
          
          <div className="px-8 py-6 bg-white/5">
            <div className="space-y-6">
              <div className="flex justify-between items-center pb-6 border-b border-white/10">
                <span className="text-green-100 text-lg font-medium">Total Expedition Cost</span>
                <span className="text-4xl font-bold text-white">
                  {formatCurrency(quote.totalPrice)}
                </span>
              </div>
              
              {quote.depositAmount > 0 && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-green-100 font-medium">Initial Deposit Required</span>
                    <span className="text-2xl font-bold text-amber-400">
                      {formatCurrency(quote.depositAmount)}
                    </span>
                  </div>
                  
                  {quote.depositDueDate && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-green-200">Deposit Due Date</span>
                      <span className="font-semibold text-green-100">
                        {formatDate(quote.depositDueDate)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Payment Button */}
            {onPayment && (
              <div className="mt-8">
                <button
                  onClick={onPayment}
                  disabled={isAgentPreview}
                  className={`w-full bg-amber-500 hover:bg-amber-600 text-green-900 font-bold py-4 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-3 ${
                    isAgentPreview ? 'opacity-50 cursor-not-allowed' : 'shadow-lg hover:shadow-xl'
                  }`}
                >
                  <CheckCircle2 className="w-5 h-5" />
                  {isAgentPreview ? 'Preview Mode' : 'Begin Your Expedition'}
                </button>
                {!isAgentPreview && (
                  <p className="text-center text-xs text-green-200 mt-4">
                    Secure booking • Adventure insurance available
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-12 bg-stone-100 rounded-2xl border-2 border-stone-300 overflow-hidden shadow-lg">
            <div className="bg-gradient-to-r from-stone-700 to-stone-600 px-6 py-4">
              <div className="flex items-center justify-center gap-2 text-amber-100">
                <Mountain className="w-6 h-6" />
                <h3 className="text-lg font-bold">Your Expedition Guide</h3>
              </div>
            </div>
            
            <div className="p-6">
              {quote.agent_name && (
                <div className="text-center mb-4">
                  <p className="text-xl font-bold text-stone-900">{quote.agent_name}</p>
                  {quote.agent_business_name && (
                    <p className="text-stone-600 font-semibold">{quote.agent_business_name}</p>
                  )}
                </div>
              )}
              
              <div className="space-y-3 max-w-md mx-auto">
                {quote.agent_email && (
                  <a 
                    href={`mailto:${quote.agent_email}`}
                    className="flex items-center gap-3 bg-white px-4 py-3 rounded-xl hover:bg-stone-50 transition-colors border-2 border-stone-300"
                  >
                    <Mail className="w-5 h-5 text-stone-700" />
                    <span className="text-stone-900 font-medium">{quote.agent_email}</span>
                  </a>
                )}
                
                {quote.agent_phone && (
                  <a 
                    href={`tel:${quote.agent_phone}`}
                    className="flex items-center gap-3 bg-white px-4 py-3 rounded-xl hover:bg-stone-50 transition-colors border-2 border-stone-300"
                  >
                    <Phone className="w-5 h-5 text-stone-700" />
                    <span className="text-stone-900 font-medium">{quote.agent_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 text-stone-500 text-sm">
            <Mountain className="w-4 h-4" />
            <span>Expedition Travel Specialists • Est. Since Day One</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpeditionAdventureTemplate;
